import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Clock, Play, Square, Plus, ChevronLeft, ChevronRight, Check, X,
  Download, AlertCircle, Timer, FileText, Users, Settings2, Pencil, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ENTRY_TYPES = [
  { value: 'regular', label: 'Regular', color: 'bg-blue-100 text-blue-800' },
  { value: 'on_call_shift', label: 'On-Call Shift', color: 'bg-amber-100 text-amber-800' },
  { value: 'on_call_worked', label: 'On-Call Hours Worked', color: 'bg-orange-100 text-orange-800' },
];
const STATUS_STYLES = {
  draft: { label: 'Draft', bg: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400' },
  employee_approved: { label: 'Employee Approved', bg: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  manager_approved: { label: 'Manager Approved', bg: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  locked: { label: 'Locked', bg: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg}`} data-testid={`status-badge-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const EntryTypeBadge = ({ type }) => {
  const t = ENTRY_TYPES.find(e => e.value === type) || ENTRY_TYPES[0];
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.color}`}>{t.label}</span>;
};

// ── Add/Edit Entry Modal ───────────────────────────────────────────────────
const EntryModal = ({ isOpen, onClose, onSave, editEntry, settings }) => {
  const [form, setForm] = useState({
    date: '', entry_type: 'regular', start_time: '08:00', end_time: '17:00', notes: '',
  });

  useEffect(() => {
    if (editEntry) {
      setForm({
        date: editEntry.date || '',
        entry_type: editEntry.entry_type || 'regular',
        start_time: editEntry.start_time || '08:00',
        end_time: editEntry.end_time || '17:00',
        notes: editEntry.notes || '',
      });
    } else {
      setForm({ date: '', entry_type: 'regular', start_time: '08:00', end_time: '17:00', notes: '' });
    }
  }, [editEntry, isOpen]);

  if (!isOpen) return null;

  const calcPreviewHours = () => {
    try {
      const [sh, sm] = form.start_time.split(':').map(Number);
      const [eh, em] = form.end_time.split(':').map(Number);
      let mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins < 0) mins += 1440;
      if (form.entry_type === 'on_call_worked') {
        const block = settings?.oncall_rounding_minutes || 15;
        mins = Math.ceil(mins / block) * block;
      }
      return (mins / 60).toFixed(2);
    } catch { return '0.00'; }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="entry-modal">
        <h3 className="text-lg font-semibold mb-4">{editEntry ? 'Edit Time Entry' : 'Add Time Entry'}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm" value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="entry-date" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entry Type</label>
            <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.entry_type}
              onChange={e => setForm(f => ({ ...f, entry_type: e.target.value }))} data-testid="entry-type-select">
              {ENTRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input type="time" className="w-full border rounded-lg px-3 py-2 text-sm" value={form.start_time}
                onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} data-testid="entry-start-time" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input type="time" className="w-full border rounded-lg px-3 py-2 text-sm" value={form.end_time}
                onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} data-testid="entry-end-time" />
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <span className="text-gray-500">Calculated Hours:</span>{' '}
            <span className="font-semibold text-gray-900" data-testid="calculated-hours">{calcPreviewHours()} hrs</span>
            {form.entry_type === 'on_call_worked' && (
              <span className="text-xs text-gray-400 ml-2">(rounded up to {settings?.oncall_rounding_minutes || 15}-min blocks)</span>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input type="text" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Optional"
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="entry-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} data-testid="entry-cancel-btn">Cancel</Button>
          <Button onClick={() => onSave(form, editEntry?.id)} data-testid="entry-save-btn">
            {editEntry ? 'Update' : 'Add Entry'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
export default function TimeTracking() {
  const { user, token, hasPermission } = useAuth();
  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'team_lead';
  const isAdmin = user?.role === 'admin';
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [activeTab, setActiveTab] = useState('timecard');
  const [clockStatus, setClockStatus] = useState({ clocked_in: false, entry: null });
  const [timecard, setTimecard] = useState(null);
  const [period, setPeriod] = useState({ start: '', end: '' });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [teamTimecards, setTeamTimecards] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);

  // Load initial data
  const loadPeriodAndSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/time/current-period`, { headers });
      setPeriod({ start: res.data.period_start, end: res.data.period_end });
      setSettings(res.data.settings);
      return res.data;
    } catch (err) {
      toast.error('Failed to load time tracking settings');
      return null;
    }
  }, [headers]);

  const loadClockStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/time/clock-status`, { headers });
      setClockStatus(res.data);
    } catch (e) {
      console.error('Failed to load clock status:', e);
    }
  }, [headers]);

  const loadTimecard = useCallback(async (start, end) => {
    if (!start || !end) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/time/timecard`, { headers, params: { period_start: start, period_end: end } });
      setTimecard(res.data);
    } catch (err) {
      toast.error('Failed to load timecard');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => {
    (async () => {
      const data = await loadPeriodAndSettings();
      if (data) {
        await loadTimecard(data.period_start, data.period_end);
        await loadClockStatus();
      }
    })();
  }, [loadPeriodAndSettings, loadTimecard, loadClockStatus]);

  // Navigate pay periods
  const navigatePeriod = async (dir) => {
    if (!settings) return;
    const ref = dir === 'prev' ? period.start : period.end;
    const d = new Date(ref + 'T12:00:00');
    d.setDate(d.getDate() + (dir === 'prev' ? -1 : 1));
    const dateStr = d.toISOString().split('T')[0];
    try {
      const res = await axios.get(`${API}/time/timecard`, {
        headers, params: { period_start: dateStr, period_end: dateStr }
      });
      setPeriod({ start: res.data.period_start, end: res.data.period_end });
      setTimecard(res.data);
    } catch { toast.error('Failed to navigate period'); }
  };

  // Clock actions
  const handleClockIn = async () => {
    try {
      await axios.post(`${API}/time/clock-in`, {}, { headers });
      toast.success('Clocked in!');
      await loadClockStatus();
      await loadTimecard(period.start, period.end);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clock in'); }
  };

  const handleClockOut = async () => {
    try {
      await axios.post(`${API}/time/clock-out`, {}, { headers });
      toast.success('Clocked out!');
      await loadClockStatus();
      await loadTimecard(period.start, period.end);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clock out'); }
  };

  // Entry CRUD
  const handleSaveEntry = async (form, entryId) => {
    try {
      if (entryId) {
        await axios.put(`${API}/time/entries/${entryId}`, form, { headers });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/time/entries`, form, { headers });
        toast.success('Entry added');
      }
      setShowEntryModal(false);
      setEditEntry(null);
      await loadTimecard(period.start, period.end);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save entry'); }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await axios.delete(`${API}/time/entries/${entryId}`, { headers });
      toast.success('Entry deleted');
      await loadTimecard(period.start, period.end);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  // Approval
  const handleEmployeeApprove = async () => {
    try {
      await axios.post(`${API}/time/timecard/employee-approve`, {}, {
        headers, params: { period_start: period.start, period_end: period.end }
      });
      toast.success('Timecard submitted for approval');
      await loadTimecard(period.start, period.end);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };

  // Team timecards
  const loadTeamTimecards = async (start, end) => {
    try {
      const res = await axios.get(`${API}/time/team-timecards`, {
        headers, params: { period_start: start || period.start, period_end: end || period.end }
      });
      setTeamTimecards(res.data);
    } catch (err) { toast.error('Failed to load team timecards'); }
  };

  const handleManagerApprove = async (userId) => {
    try {
      await axios.post(`${API}/time/timecard/${userId}/manager-approve`, {}, {
        headers, params: { period_start: period.start, period_end: period.end }
      });
      toast.success('Timecard approved');
      await loadTeamTimecards();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };

  const handleManagerReject = async (userId) => {
    const notes = window.prompt('Rejection reason (optional):');
    if (notes === null) return;
    try {
      await axios.post(`${API}/time/timecard/${userId}/reject`, { notes }, {
        headers, params: { period_start: period.start, period_end: period.end }
      });
      toast.success('Timecard sent back to employee');
      await loadTeamTimecards();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
  };

  // Export
  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/time/export`, {
        headers, params: { period_start: period.start, period_end: period.end },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll-export-${period.start}-to-${period.end}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (err) { toast.error('Export failed'); }
  };

  // Settings
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/time/settings`, settingsForm, { headers });
      toast.success('Settings saved');
      const data = await loadPeriodAndSettings();
      if (data) await loadTimecard(data.period_start, data.period_end);
    } catch (err) { toast.error('Failed to save settings'); }
  };

  // Group entries by date for the timecard grid
  const buildDailyGrid = () => {
    if (!timecard) return [];
    const entries = timecard.entries || [];
    const start = new Date(period.start + 'T12:00:00');
    const end = new Date(period.end + 'T12:00:00');
    const days = [];
    const d = new Date(start);
    while (d <= end) {
      const ds = d.toISOString().split('T')[0];
      const dayEntries = entries.filter(e => e.date === ds);
      const total = dayEntries.reduce((s, e) => s + (e.hours || 0), 0);
      days.push({ date: ds, dayName: DAY_NAMES[d.getDay()], entries: dayEntries, total: round2(total) });
      d.setDate(d.getDate() + 1);
    }
    return days;
  };

  const round2 = (n) => Math.round(n * 100) / 100;
  const isLocked = timecard?.status === 'manager_approved' || timecard?.status === 'locked';
  const empType = user?.employment_type || timecard?.employment_type || 'hourly';
  const isNonExempt = empType === 'hourly';

  const formatPeriodLabel = () => {
    if (!period.start) return '';
    const s = new Date(period.start + 'T12:00:00');
    const e = new Date(period.end + 'T12:00:00');
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Tab definitions
  const tabs = [
    { id: 'timecard', label: 'My Timecard', icon: FileText },
    ...(isManager ? [{ id: 'team', label: 'Team Review', icon: Users }] : []),
    ...(isAdmin ? [{ id: 'export', label: 'Export', icon: Download }] : []),
    ...(isAdmin ? [{ id: 'settings', label: 'Settings', icon: Settings2 }] : []),
  ];

  return (
    <div className="space-y-6" data-testid="time-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="time-tracking-title">Time Tracking</h1>
          <p className="text-sm text-gray-500 mt-1">Track hours, manage timecards, and export for payroll</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" data-testid="time-tracking-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => {
              setActiveTab(t.id);
              if (t.id === 'team') loadTeamTimecards();
              if (t.id === 'settings' && settings && !settingsForm) setSettingsForm({ ...settings });
            }}
              className={`flex items-center gap-2 pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── My Timecard Tab ───────────────────────────────────── */}
      {activeTab === 'timecard' && (
        <div className="space-y-6">
          {/* Clock Widget — non-exempt only */}
          {isNonExempt && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="clock-widget">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                    clockStatus.clocked_in ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <Timer className={`h-7 w-7 ${clockStatus.clocked_in ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Time Clock</p>
                    <p className={`text-lg font-semibold ${clockStatus.clocked_in ? 'text-green-700' : 'text-gray-700'}`}
                      data-testid="clock-status-text">
                      {clockStatus.clocked_in ? 'Currently Clocked In' : 'Not Clocked In'}
                    </p>
                    {clockStatus.clocked_in && clockStatus.entry?.clock_in && (
                      <p className="text-xs text-gray-400">
                        Since {new Date(clockStatus.entry.clock_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
                {clockStatus.clocked_in ? (
                  <Button onClick={handleClockOut} variant="destructive" size="lg" className="gap-2" data-testid="clock-out-btn">
                    <Square className="h-4 w-4" /> Clock Out
                  </Button>
                ) : (
                  <Button onClick={handleClockIn} size="lg" className="gap-2 bg-green-600 hover:bg-green-700" data-testid="clock-in-btn">
                    <Play className="h-4 w-4" /> Clock In
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Period Navigator */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => navigatePeriod('prev')} data-testid="period-prev">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900" data-testid="period-label">{formatPeriodLabel()}</h2>
              <div className="flex items-center justify-center gap-2 mt-1">
                <StatusBadge status={timecard?.status || 'draft'} />
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigatePeriod('next')} data-testid="period-next">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {/* Rejection Notice */}
          {timecard?.timecard?.rejected_at && timecard?.status === 'draft' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="rejection-notice">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Timecard returned by {timecard.timecard.rejected_by_name || 'Manager'}</p>
                {timecard.timecard.rejection_notes && (
                  <p className="text-sm text-red-600 mt-1">{timecard.timecard.rejection_notes}</p>
                )}
              </div>
            </div>
          )}

          {/* Weekly Timecard Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="timecard-grid">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-4 py-3 font-medium text-gray-600 w-24">Day</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 w-28">Date</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">In</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Out</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600 w-20">Hours</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600 w-28">Daily Total</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600 w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildDailyGrid().map((day, di) => {
                      const isWeekend = day.dayName === 'Sat' || day.dayName === 'Sun';
                      const rowBg = isWeekend ? 'bg-gray-50/50' : '';
                      if (day.entries.length === 0) {
                        return (
                          <tr key={day.date} className={`border-b hover:bg-gray-50 ${rowBg}`} data-testid={`day-row-${day.date}`}>
                            <td className="px-4 py-3 font-medium text-gray-500">{day.dayName}</td>
                            <td className="px-4 py-3 text-gray-700">{new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })}</td>
                            <td className="px-4 py-3 text-gray-400">-</td>
                            <td className="px-4 py-3 text-gray-400">-</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-right text-gray-400">0.00</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-400">0.00</td>
                            <td className="px-4 py-3 text-center">
                              {!isLocked && (
                                <button onClick={() => { setEditEntry(null); setShowEntryModal(true); }}
                                  className="text-gray-400 hover:text-blue-600" data-testid={`add-entry-${day.date}`}>
                                  <Plus className="h-4 w-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      }
                      return day.entries.map((entry, ei) => (
                        <tr key={entry.id} className={`border-b hover:bg-gray-50 ${rowBg}`} data-testid={`entry-row-${entry.id}`}>
                          {ei === 0 && (
                            <>
                              <td className="px-4 py-3 font-medium text-gray-700" rowSpan={day.entries.length}>{day.dayName}</td>
                              <td className="px-4 py-3 text-gray-700" rowSpan={day.entries.length}>
                                {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })}
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 text-gray-700">{entry.start_time || '-'}</td>
                          <td className="px-4 py-3 text-gray-700">{entry.end_time || (entry.source === 'clock' && !entry.clock_out ? <span className="text-green-600 font-medium">Active</span> : '-')}</td>
                          <td className="px-4 py-3"><EntryTypeBadge type={entry.entry_type} /></td>
                          <td className="px-4 py-3 text-right font-mono text-gray-900">{entry.hours?.toFixed(2) || '0.00'}</td>
                          {ei === 0 && (
                            <td className="px-4 py-3 text-right font-semibold text-gray-900" rowSpan={day.entries.length}>
                              {day.total.toFixed(2)}
                            </td>
                          )}
                          <td className="px-4 py-3 text-center">
                            {!isLocked && entry.source !== 'clock' && (
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => { setEditEntry(entry); setShowEntryModal(true); }}
                                  className="text-gray-400 hover:text-blue-600" data-testid={`edit-entry-${entry.id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => handleDeleteEntry(entry.id)}
                                  className="text-gray-400 hover:text-red-600" data-testid={`delete-entry-${entry.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              {timecard?.totals && (
                <div className="border-t bg-gray-50 p-4" data-testid="timecard-totals">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Regular</span>
                      <p className="font-semibold text-gray-900">{timecard.totals.regular_hours?.toFixed(2)} hrs</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Overtime</span>
                      <p className="font-semibold text-red-600">{timecard.totals.overtime_hours?.toFixed(2)} hrs</p>
                    </div>
                    <div>
                      <span className="text-gray-500">On-Call Shift</span>
                      <p className="font-semibold text-amber-600">{timecard.totals.oncall_shift_hours?.toFixed(2)} hrs</p>
                    </div>
                    <div>
                      <span className="text-gray-500">On-Call Worked</span>
                      <p className="font-semibold text-orange-600">{timecard.totals.oncall_worked_hours?.toFixed(2)} hrs</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Total</span>
                      <p className="font-bold text-lg text-gray-900">{timecard.totals.total_hours?.toFixed(2)} hrs</p>
                    </div>
                  </div>
                  {/* Weekly breakdowns */}
                  {timecard.weekly_summaries?.length > 1 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2 font-medium">Weekly Breakdown</p>
                      <div className="space-y-1">
                        {timecard.weekly_summaries.map((w, i) => (
                          <div key={w.week_start || i} className="flex items-center justify-between text-xs text-gray-600">
                            <span>Week {i + 1}: {new Date(w.week_start + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(w.week_end + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <span className="font-medium">{w.total_hours?.toFixed(2)} hrs {w.overtime_hours > 0 ? <span className="text-red-500">(+{w.overtime_hours?.toFixed(2)} OT)</span> : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => { setEditEntry(null); setShowEntryModal(true); }}
              disabled={isLocked} className="gap-2" data-testid="add-entry-btn">
              <Plus className="h-4 w-4" /> Add Entry
            </Button>
            <div className="flex gap-2">
              {timecard?.status === 'draft' && (
                <Button onClick={handleEmployeeApprove} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="employee-approve-btn">
                  <Check className="h-4 w-4" /> Submit & Approve
                </Button>
              )}
              {timecard?.status === 'employee_approved' && !isManager && (
                <span className="text-sm text-gray-500 py-2">Waiting for manager approval...</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Team Review Tab ───────────────────────────────────── */}
      {activeTab === 'team' && isManager && (
        <div className="space-y-4" data-testid="team-review-tab">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Team Timecards</h2>
            <span className="text-sm text-gray-500">{formatPeriodLabel()}</span>
          </div>
          {!teamTimecards ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Employee</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Regular</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">OT</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">On-Call</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Total</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(teamTimecards.timecards || []).filter(t => t.entry_count > 0 || t.status !== 'draft').map(tc => (
                    <tr key={tc.user_id} className="border-b hover:bg-gray-50" data-testid={`team-row-${tc.user_id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{tc.user_name}</p>
                        <p className="text-xs text-gray-500">{tc.role}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${tc.employment_type === 'hourly' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {tc.employment_type === 'hourly' ? 'Non-Exempt' : 'Exempt'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{tc.regular_hours?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono text-red-600">{tc.overtime_hours?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono text-amber-600">{(tc.oncall_shift_hours + tc.oncall_worked_hours).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{tc.total_hours?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={tc.status} /></td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {tc.status === 'employee_approved' && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleManagerApprove(tc.user_id)}
                                data-testid={`approve-btn-${tc.user_id}`}>
                                <Check className="h-3 w-3" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleManagerReject(tc.user_id)} data-testid={`reject-btn-${tc.user_id}`}>
                                <X className="h-3 w-3" /> Reject
                              </Button>
                            </>
                          )}
                          {tc.status === 'draft' && <span className="text-xs text-gray-400">Pending employee</span>}
                          {tc.status === 'manager_approved' && <span className="text-xs text-green-600">Approved</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(teamTimecards.timecards || []).filter(t => t.entry_count > 0 || t.status !== 'draft').length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">No timecards submitted for this period</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Export Tab ────────────────────────────────────────── */}
      {activeTab === 'export' && isAdmin && (
        <div className="space-y-6" data-testid="export-tab">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm max-w-lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">ADP Payroll Export</h2>
            <p className="text-sm text-gray-500 mb-6">
              Export timecard data in ADP Workforce Now-compatible CSV format for the selected pay period.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Period:</span> {formatPeriodLabel()}
              </p>
              <p className="text-xs text-gray-400 mt-1">Use the period navigator on the My Timecard tab to change periods</p>
            </div>
            <Button onClick={handleExport} className="gap-2" data-testid="export-btn">
              <Download className="h-4 w-4" /> Download ADP CSV
            </Button>
          </div>
        </div>
      )}

      {/* ── Settings Tab ─────────────────────────────────────── */}
      {activeTab === 'settings' && isAdmin && settingsForm && (
        <div className="space-y-6" data-testid="settings-tab">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Time Tracking Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay Period Type</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={settingsForm.pay_period_type}
                  onChange={e => setSettingsForm(f => ({ ...f, pay_period_type: e.target.value }))} data-testid="setting-pay-period-type">
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="semi_monthly">Semi-Monthly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period Anchor Date</label>
                <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm" value={settingsForm.pay_period_anchor_date}
                  onChange={e => setSettingsForm(f => ({ ...f, pay_period_anchor_date: e.target.value }))} data-testid="setting-anchor-date" />
                <p className="text-xs text-gray-400 mt-1">A known pay period start date for calculation reference</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weekly OT Threshold (hours)</label>
                <input type="number" step="0.5" className="w-full border rounded-lg px-3 py-2 text-sm" value={settingsForm.overtime_threshold_weekly}
                  onChange={e => setSettingsForm(f => ({ ...f, overtime_threshold_weekly: parseFloat(e.target.value) || 40 }))} data-testid="setting-ot-threshold" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OT Multiplier</label>
                <input type="number" step="0.1" className="w-full border rounded-lg px-3 py-2 text-sm" value={settingsForm.overtime_multiplier}
                  onChange={e => setSettingsForm(f => ({ ...f, overtime_multiplier: parseFloat(e.target.value) || 1.5 }))} data-testid="setting-ot-multiplier" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">On-Call Flat Rate ($)</label>
                <input type="number" step="1" className="w-full border rounded-lg px-3 py-2 text-sm" value={settingsForm.oncall_flat_rate}
                  onChange={e => setSettingsForm(f => ({ ...f, oncall_flat_rate: parseFloat(e.target.value) || 30 }))} data-testid="setting-oncall-rate" />
                <p className="text-xs text-gray-400 mt-1">Flat rate per {settingsForm.oncall_shift_duration || 8}-hour on-call shift</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">On-Call Rounding (minutes)</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={settingsForm.oncall_rounding_minutes}
                  onChange={e => setSettingsForm(f => ({ ...f, oncall_rounding_minutes: parseInt(e.target.value) }))} data-testid="setting-oncall-rounding">
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                </select>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t flex justify-end">
              <Button onClick={handleSaveSettings} data-testid="save-settings-btn">Save Settings</Button>
            </div>
          </div>
        </div>
      )}

      {/* Entry Modal */}
      <EntryModal
        isOpen={showEntryModal}
        onClose={() => { setShowEntryModal(false); setEditEntry(null); }}
        onSave={handleSaveEntry}
        editEntry={editEntry}
        settings={settings}
      />
    </div>
  );
}
